import { FC, FormEvent, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  selectProviderFilesState,
  Creators as ProviderFilesActions,
} from '../../modules/provider-files.module';
import {
  Creators as LoadingActions,
  selectLoadingState,
} from '../../modules/loading.module';
import { v4 as uuidv4 } from 'uuid';
import {
  deleteObject,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { storage } from '../../utils/Firebase';
import { selectProviderState } from '../../modules/provider.module';
import { useBoundedActions } from '../../hooks/useBoundedActions';

type Props = {
  onGoBack: () => void;
};

export const FilesPage: FC<Props> = ({ onGoBack }) => {
  const files = useSelector(selectProviderFilesState);
  const { loading } = useSelector(selectLoadingState);
  const provider = useSelector(selectProviderState);
  const { requestUploadProviderFile, requestDeleteProviderFile } =
    useBoundedActions(ProviderFilesActions);
  const { setLoading, setNotLoading } = useBoundedActions(LoadingActions);

  const isAnyFileNotUploaded = files.some(
    (file) => file.required && file.files.length === 0,
  );

  const uploadFile = useCallback(
    (categoryId: number) => async (e: FormEvent) => {
      const target = e.target as HTMLInputElement;

      if (!target.files?.length) {
        return;
      }

      const file = target.files.item(target.files.length - 1);

      if (!file) {
        return;
      }

      const reference = `provider-files/${provider.id}/${uuidv4()}/${file.name}`;

      try {
        setLoading();

        // Upload file to Firebase Storage
        await uploadBytes(ref(storage, reference), file);

        // Register file in backend
        requestUploadProviderFile({
          categoryId,
          fileName: reference,
        });

        // Clear the input
        target.value = '';
      } catch (error) {
        setNotLoading();
        console.error('Error uploading file:', error);
        // Optionally clean up Firebase storage if backend registration fails
        try {
          await deleteObject(ref(storage, reference));
        } catch (deleteError) {
          console.error('Error deleting file from storage:', deleteError);
        }
      }
    },
    [provider.id, requestUploadProviderFile, setLoading, setNotLoading],
  );

  const openFile = useCallback(async (fileName: string) => {
    try {
      const url = await getDownloadURL(ref(storage, fileName));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening file:', error);
    }
  }, []);

  const deleteFile = useCallback(
    (fileId: number, displayName: string) => {
      if (
        window.confirm(
          `Are you sure you want to delete "${displayName}"? This action cannot be undone.`,
        )
      ) {
        requestDeleteProviderFile({ fileId });
      }
    },
    [requestDeleteProviderFile],
  );

  return (
    <div>
      <h2 className="text-primary">Files</h2>

      <ol>
        {files.map((fileCategoryItem, index) => (
          <li key={fileCategoryItem.categoryId} className="mb-1">
            <div className="flex-row d-flex align-items-center gap-2">
              <span>
                <span className="fw-bold">{fileCategoryItem.categoryName}</span>
                {fileCategoryItem.required && (
                  <span className="text-danger ml-1">*</span>
                )}
              </span>
              <label
                style={{ display: 'inline-flex' }}
                htmlFor={`file-${fileCategoryItem.categoryId}`}
              >
                <img
                  className="send-btn"
                  src="/assets/file.png"
                  alt="File uploader"
                />
              </label>

              <input
                className="d-none"
                type="file"
                id={`file-${fileCategoryItem.categoryId}`}
                name={`file-${fileCategoryItem.categoryId}`}
                accept="application/pdf, image/png, image/jpeg"
                onChange={uploadFile(fileCategoryItem.categoryId)}
                disabled={loading}
              />
            </div>
            <ul>
              {fileCategoryItem.files.map((file) => {
                const isFileWithPendingStatus = file.status === 'pending';

                return (
                  <li key={file.id}>
                    <span
                      className="fw-bold fst-italic text-decoration-underline cursor-pointer"
                      onClick={() => openFile(file.name)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          openFile(file.name);
                        }
                      }}
                    >
                      {file.displayName}
                    </span>
                    <span
                      className={`${isFileWithPendingStatus ? 'text-warning-emphasis' : 'text-success'}`}
                    >
                      {' '}
                      – {file.status}
                    </span>

                    {isFileWithPendingStatus && (
                      <Button
                        variant="primary"
                        className="ml-3"
                        onClick={() => deleteFile(file.id, file.displayName)}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>

      {!isAnyFileNotUploaded && (
        <Button
          variant="outline-dark"
          className="height-30 margin-top-35 padding-h-50"
          onClick={onGoBack}
          disabled={loading}
        >
          <h6>Back</h6>
        </Button>
      )}
    </div>
  );
};
